import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useBrandProfile } from "../../../hooks/useBrandProfile";
import { mensajeDeBloqueo, motivoDeBloqueo, puedeContinuar } from "../datos/continuarPuestaEnMarcha";
import { estadoDeFuente, type EstadoConexionFuente } from "../datos/estadoFuente";
import { useFuentesConectadas } from "../datos/fuentesConectadas";
import { useCanalesReferencia } from "../datos/useCanalesReferencia";
import { useMaterialesConocimiento } from "../datos/useMaterialesConocimiento";
import { MarcoLogo, type MarcaLogo } from "./LogosMarcas";
import { ModalAnadirFuente, type AvisoAnadido } from "./ModalAnadirFuente";
import { PieDePaso, type AvisoPie, type PropsPasoPuestaEnMarcha } from "./PieDePaso";
import { AccionesFuente, CLASE_BOTON_PRIMARIO, PildoraEstado } from "./piezasConexion";
import { CLASE_TARJETA_BASE } from "./tarjetasFuente";

/**
 * Paso 1 de la Puesta en marcha: las cuatro fuentes, una arriba de la otra.
 *
 * APILADAS Y NO EN GRILLA DE TRES. En "Fuentes de datos" las conexiones son
 * tres tarjetas altas en columnas porque ahí se están comparando; acá son una
 * LISTA DE COSAS POR HACER, y una lista se lee de arriba abajo. Cada fila usa
 * `CLASE_TARJETA_BASE` —la misma forma que las fuentes cargadas, sin su alto mínimo— con
 * el logo a la izquierda, el nombre y el detalle en el medio, y el estado más
 * la acción a la derecha.
 *
 * LOS ESTADOS Y LOS DESTINOS SON LOS QUE YA EXISTEN. El estado sale de
 * `useFuentesConectadas` + `estadoDeFuente`, y los botones van exactamente a
 * donde manda `SeccionConexiones`: Instagram a Mi marca, Tiendanube a Mi marca con `?conectar=tiendanube`. No hay
 * un segundo camino para conectar nada: hay UNO, y esta pantalla lo usa. Los
 * cuatro estados (conectado / sin conectar / cargando / falló la consulta) se
 * dibujan con las mismas piezas (`piezasConexion.tsx`), así que "falló" nunca
 * se ve como "sin conectar".
 *
 * Ver ADR-2026-08-17-estudio-marketing-puesta-en-marcha.
 */

/** Instagram se conecta por marca en Mi marca (ADR-2026-09-24-instagram-boton-en-mi-marca). */
const RUTA_INSTAGRAM = "/mi-marca";
const RUTA_TIENDANUBE = "/mi-marca";
const RUTA_TIENDANUBE_CONECTAR = "/mi-marca?conectar=tiendanube";

/** Las tres plataformas de las que se pueden traer creadores, en el mismo orden
 *  que el atajo del modal. */
const LOGOS_CREADORES: MarcaLogo[] = ["youtube", "instagram", "tiktok"];

/**
 * HOY ESTE PASO NO BLOQUEA. Matías pidió poder pasar al siguiente sin tener
 * ninguna fuente conectada, para probar el flujo entero de punta a punta.
 *
 * La regla real sigue existiendo y sigue testeada (`continuarPuestaEnMarcha.ts`
 * con sus mensajes por motivo): lo único que cambia es SI se usa para
 * deshabilitar el botón. Volver a exigir la conexión es poner esto en `true` —
 * una línea, sin reescribir el pie ni los mensajes. Se deja como constante y no
 * borrando la llamada porque borrarla se lleva puestos los tres mensajes de
 * bloqueo, que son la parte cara.
 */
const EXIGE_CONEXION = false;

const DETALLE_INSTAGRAM: Record<EstadoConexionFuente, string> = {
  cargando: "Estamos consultando el estado de tu cuenta.",
  conectado: "Tu cuenta está vinculada: Atlas puede publicar y programar en tu feed.",
  "sin-conectar": "Conectala para que Atlas pueda publicar y programar en tu feed.",
  error: "No pudimos consultar esta conexión. Puede ser un problema de red — probá de nuevo.",
  "no-disponible": "",
};

const DETALLE_TIENDANUBE: Record<EstadoConexionFuente, string> = {
  cargando: "Estamos consultando el estado de tu tienda.",
  conectado: "Atlas está leyendo tu catálogo y tus productos.",
  "sin-conectar": "Conectala para que Atlas conozca tu catálogo y tus ventas.",
  error: "No pudimos consultar esta conexión. Puede ser un problema de red — probá de nuevo.",
  "no-disponible": "",
};

/**
 * Una fila. El logo, el nombre con su detalle, y a la derecha lo que haya:
 * la píldora de estado y la acción.
 *
 * `apagado` es para Meta Ads, que no tiene integración: fondo y logo grises,
 * como en las tarjetas de Fuentes de datos. Se ve distinto SIN texto de por
 * medio.
 */
const Fila = ({
  logos,
  nombre,
  detalle,
  apagado = false,
  estado,
  acciones,
  vivo = false,
}: {
  logos: MarcaLogo[];
  nombre: string;
  detalle: string;
  apagado?: boolean;
  /** La píldora, o `null` en la fila que no es una conexión. */
  estado: ReactNode;
  acciones: ReactNode;
  /**
   * La fila anuncia sus cambios sola (`aria-live`). Va en la FILA ENTERA y no
   * en la píldora porque cuando vuelve la consulta cambian tres cosas a la vez
   * —la píldora, el detalle y el rótulo del botón— y las dos últimas son la
   * mitad accionable del mensaje ("probá de nuevo", "Reintentar"). Con la
   * región sobre la píldora sola, el lector anunciaba el diagnóstico y se
   * callaba la instrucción.
   *
   * Sólo lo piden las filas cuyo estado se consulta: en la de Meta Ads no
   * cambia nunca nada, y la de creadores ya tiene su propio `role="status"`
   * (el aviso de "Agregamos …"), así que marcarla viva duplicaría el anuncio.
   */
  vivo?: boolean;
}) => (
  <li className="flex">
    {/* `CLASE_TARJETA_BASE` y no `CLASE_TARJETA`: la base trae la forma (radio,
        padding horizontal, `items-center`, `gap`) SIN el alto mínimo de 108.
        Ese 108 está calculado para una tarjeta de un tercio de ancho, donde el
        texto ocupa dos líneas; acá la fila va a lo ancho de la pantalla, entra
        todo en un renglón y se veía enorme. El primer intento fue apilarle un
        `min-h-0` a la clase completa y NO funcionó: entre dos utilidades de
        Tailwind que compiten gana la que la hoja genera después, no la que se
        escribe última. Por eso el mínimo se sacó de la clase compartida en vez
        de intentar pisarlo. El alto lo da el contenido: ~64 con el `py-3`. */}
    <div
      aria-live={vivo ? "polite" : undefined}
      className={`${CLASE_TARJETA_BASE} border border-[#02062B]/[0.08] py-3 ${
        apagado ? "bg-[#FAFBFD]" : "bg-white"
      }`}
    >
      {/* Los logos apenas montados uno sobre otro cuando hay más de uno (los
          creadores): dicen de qué plataformas se puede traer sin escribirlo. */}
      <span className="flex shrink-0 -space-x-4" aria-hidden="true">
        {logos.map((marca) => (
          <MarcoLogo key={marca} marca={marca} apagado={apagado} />
        ))}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-brand-black">{nombre}</span>
        <span className="block text-[12px] leading-relaxed text-[#02062B]/[0.5]">{detalle}</span>
      </span>
      <span className="shrink-0">{estado}</span>
      <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">{acciones}</span>
    </div>
  </li>
);

export const PasoConexiones = ({
  siguiente,
  onContinuar,
  anterior,
  onVolver,
}: PropsPasoPuestaEnMarcha) => {
  const navigate = useNavigate();
  const { profile, currentBrandId, loading, loadingBrands, refresh } = useBrandProfile();
  const fuentes = useFuentesConectadas(profile?.id_company ?? null, currentBrandId);
  const canales = useCanalesReferencia(currentBrandId);
  const materiales = useMaterialesConocimiento(currentBrandId);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [aviso, setAviso] = useState<AvisoAnadido | null>(null);
  /** El botón que abre el modal: ahí vuelve el foco al cerrarlo. */
  const botonCreadoresRef = useRef<HTMLButtonElement>(null);

  const cargandoPerfil = loading || loadingBrands;
  /**
   * El perfil terminó de cargar y quedó en `null`: `fetchProfile` se traga
   * TODOS los errores, así que una red caída o RLS rota dejan `profile === null`
   * con `loading: false`. Sin esto, con `id_company` en `null`
   * `useFuentesConectadas` ni consulta la conexión de Instagram y la fila caería en
   * "Sin conectar" — mandando a reconectar por OAuth algo que puede estar
   * andando. Mismo cuarto camino que trata `ConfiguracionDatosPage`.
   */
  const perfilFallo = !cargandoPerfil && profile === null;
  const cargando = cargandoPerfil || fuentes.cargando;

  /** Distingue "cargando porque recién abrí" de "cargando porque apreté
   *  Reintentar": sólo en el segundo caso hay foco que cuidar y un rótulo que
   *  no puede volver a decir "Conectar X". */
  const [reintentando, setReintentando] = useState(false);
  useEffect(() => {
    if (!cargando) setReintentando(false);
  }, [cargando]);
  const pedirReintento = () => {
    setReintentando(true);
    // Reintenta las DOS cosas que pudieron fallar: el perfil (de donde sale el
    // `id_company`) y las consultas de las fuentes. `refresh` va envuelto en
    // una arrow porque recibe `silent` como primer parámetro.
    void refresh();
    fuentes.reintentar();
  };

  const estadoInstagram = estadoDeFuente({
    cargando,
    conectado: fuentes.instagram,
    fallo: fuentes.falloInstagram || perfilFallo,
  });
  const estadoTiendanube = estadoDeFuente({
    cargando,
    conectado: fuentes.tiendanube,
    fallo: fuentes.falloTiendanube || perfilFallo,
  });
  /**
   * Meta Ads no tiene integración: no se consulta nada, así que no puede estar
   * ni conectada ni caída. Va como constante tipada y no como literal suelto
   * para que el día que exista la integración esta línea pase a ser un
   * `estadoDeFuente({ … })` como las otras dos y NADA más cambie — ni la fila,
   * ni la regla del botón, que ya pregunta por Instagram O Meta.
   */
  const estadoMetaAds: EstadoConexionFuente = "no-disponible";

  const irAInstagram = () => navigate(RUTA_INSTAGRAM);

  const estados = { instagram: estadoInstagram, metaAds: estadoMetaAds };
  const habilitado = !EXIGE_CONEXION || puedeContinuar(estados);
  // Sin bloqueo no hay motivo que mostrar: un aviso de "conectá Instagram para
  // seguir" al lado de un botón que anda sería peor que no decir nada.
  const motivo = habilitado ? null : motivoDeBloqueo(estados);
  const avisoMotivo: AvisoPie | null = motivo
    ? {
        // `mensajeDeBloqueo` y no `MENSAJE_BLOQUEO[motivo]`: el texto de "sin
        // conectar" nombra sólo las fuentes que hoy se pueden conectar. Con
        // Meta Ads en `no-disponible`, pedir "conectá Instagram o Meta" manda
        // al usuario a buscar un botón que la fila de al lado —visible al mismo
        // tiempo, sin un clic de por medio— dice que no existe.
        texto: mensajeDeBloqueo(motivo, estados),
        // Rojo sólo para lo que salió mal (la consulta que no respondió); lo
        // que falta hacer no es un error.
        tono: motivo === "sin-respuesta" ? "alerta" : "neutro",
      }
    : null;

  const cargados = canales.agregadas.length;

  /**
   * Dónde queda lo que se acaba de cargar. Los tres desenlaces se cuentan
   * distinto porque terminan distinto: guardado en el navegador, esperando a
   * que cargue la marca (se guarda solo apenas resuelve) o directamente sin
   * poder guardar (se pierde al recargar). Mismo criterio que
   * `CanalesReferencia`.
   */
  const persisteTodo = canales.persiste && materiales.persiste;
  const sinPersistirPorStorage =
    canales.motivoSinPersistir === "storage" || materiales.motivoSinPersistir === "storage";
  const cola = persisteTodo
    ? " Queda guardado en este navegador —no en tu cuenta— y Atlas todavía no lo lee."
    : sinPersistirPorStorage
      ? " Pero este navegador no nos deja guardarlo: vale mientras no cierres la pestaña."
      : " Se termina de guardar apenas cargue tu marca.";

  return (
    <div className="flex flex-1 flex-col">
      {/* El bloque de tarjetas se centra en el alto que sobra entre el
          encabezado y el pie, y se limita a 460px de ancho. Ese ancho es el de
          una tarjeta de "Fuentes cargadas" (un tercio de la grilla de tres
          columnas, descontados los dos gaps), más un poco de aire que pidió
          Matías después. A lo ancho de la pantalla se veían enormes por más que
          se les bajara el alto. Apiladas, como también pidió. */}
      <div className="flex flex-1 flex-col items-center justify-center">
      <ul className="w-full max-w-[520px] space-y-3">
        <Fila
          logos={["instagram"]}
          nombre="Instagram"
          detalle={DETALLE_INSTAGRAM[estadoInstagram]}
          vivo
          estado={<PildoraEstado estado={estadoInstagram} />}
          acciones={
            <AccionesFuente
              estado={estadoInstagram}
              reintentando={reintentando}
              onReintentar={pedirReintento}
              textoConectar="Conectar Instagram"
              onConectar={irAInstagram}
              textoIr="Ir a la configuración"
              onIr={irAInstagram}
            />
          }
        />

        {/* Sin botón, como en Fuentes de datos: no hay a dónde mandarlo. Un
            botón que no hace nada es peor que no tenerlo. */}
        <Fila
          logos={["meta"]}
          nombre="Meta Ads"
          detalle="Todavía no existe la integración: Atlas no lee nada de tu pauta. Cuando esté, se conecta desde acá."
          apagado
          estado={<PildoraEstado estado={estadoMetaAds} />}
          acciones={null}
        />

        <Fila
          logos={["tiendanube"]}
          nombre="Tiendanube"
          detalle={DETALLE_TIENDANUBE[estadoTiendanube]}
          vivo
          estado={<PildoraEstado estado={estadoTiendanube} />}
          acciones={
            <AccionesFuente
              estado={estadoTiendanube}
              reintentando={reintentando}
              onReintentar={pedirReintento}
              textoConectar="Conectar Tiendanube"
              onConectar={() => navigate(RUTA_TIENDANUBE_CONECTAR)}
              textoIr="Ir a Mi marca"
              onIr={() => navigate(RUTA_TIENDANUBE)}
            />
          }
        />

        {/* La cuarta no es una conexión: no hay cuenta que vincular ni estado
            que consultar. Por eso no lleva píldora de conexión sino el conteo
            de lo cargado, que es lo único que se puede afirmar de ella. */}
        <Fila
          logos={LOGOS_CREADORES}
          nombre="Canales de creadores"
          detalle="Pegá links de videos o vlogs de creadores para que el agente piense como ellos."
          estado={
            cargados > 0 ? (
              <span className="inline-flex items-center rounded-full bg-[#F0F2F7] py-1 px-2.5 text-[11.5px] font-medium text-[#02062B]/[0.55]">
                {cargados} {cargados === 1 ? "cargado" : "cargados"}
              </span>
            ) : null
          }
          acciones={
            <button
              type="button"
              ref={botonCreadoresRef}
              onClick={() => setModalAbierto(true)}
              // `CLASE_BOTON_PRIMARIO` es el mismo primario de las tres filas
              // de arriba (y ya trae su anillo de foco): la acción de esta fila
              // se ve igual que la de conectar, porque es del mismo rango.
              className={`inline-flex items-center gap-1.5 ${CLASE_BOTON_PRIMARIO}`}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Pegar links
            </button>
          }
        />
      </ul>

      {aviso && (
        <p role="status" className="mt-3 text-[12.5px] leading-relaxed text-[#1D7A57]">
          {aviso.clase === "canal" ? `Agregamos “${aviso.nombre}” a tus canales.` : aviso.texto}
          {cola}
        </p>
      )}

      </div>

      <PieDePaso
        habilitado={habilitado}
        motivo={avisoMotivo}
        siguiente={siguiente}
        onContinuar={onContinuar}
        anterior={anterior}
        onVolver={onVolver}
      />

      {modalAbierto && (
        // EL MISMO modal de "Añadir fuente", abierto directo en su sub-pantalla
        // de video: no se duplica ni se reescribe (son 844 líneas con foco
        // atrapado, navegación interna y textos ya revisados). Agrega por el
        // MISMO camino que Fuentes de datos —mismo dedup, mismos rechazos,
        // misma persistencia por marca—, así que lo que se cargue acá aparece
        // allá y al revés.
        <ModalAnadirFuente
          pantallaInicial="video"
          onCerrar={() => setModalAbierto(false)}
          volverElFocoA={botonCreadoresRef}
          agregarCanal={canales.agregar}
          agregarMateriales={materiales.agregar}
          onGuardado={setAviso}
        />
      )}
    </div>
  );
};

export default PasoConexiones;
